import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from 'react-aria-components';
import eventBus, { CUSTOM_EVENTS } from '../../utils/EventBus';

type PortalSearchProps = Readonly<{
  open: boolean;
  setOpen: (openState: boolean) => void;
  searchTerm: string | null;
  updateSearchTerm: (searchTerm: string) => void;
}>;

export default function PortalSearch({ open, searchTerm, updateSearchTerm }: PortalSearchProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [init, setInit] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(searchTerm ?? '');

  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus();
    }
  }, [open]);

  // On mount, set the initial search term state
  useEffect(() => {
    if (searchTerm !== null && !init) {
      setInputValue(searchTerm);
      setInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This should only fire on initial external search term update
  }, [searchTerm]);

  /**
   * Event listener to clear search, filters are cleared in PortalFilters component
   */
  useEffect(() => {
    const clearSearch = () => {
      setInputValue('');
      updateSearchTerm('');
      searchInputRef.current?.focus();
    };

    eventBus.on(CUSTOM_EVENTS.PORTAL_CLEAR_FILTERS_AND_SEARCH, clearSearch);
    return () => {
      eventBus.off(CUSTOM_EVENTS.PORTAL_CLEAR_FILTERS_AND_SEARCH, clearSearch);
    };
  }, [updateSearchTerm]);

  return (
    <div className="portal-search-bar__wrapper">
      <div className={clsx('portal-search-bar', { 'portal-search-bar--active': open })}>
        <input
          ref={searchInputRef}
          aria-label="Enter a search term"
          tabIndex={open ? 0 : -1}
          className="portal-search-bar__input"
          type="text"
          name="search"
          value={inputValue}
          onChange={e => setInputValue(e.currentTarget.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              updateSearchTerm(inputValue);
            }
          }}
        />
        <Button
          className="portal-search-bar__btn"
          aria-label="Submit search term"
          excludeFromTabOrder={!open}
          onClick={() => updateSearchTerm(inputValue)}
        >
          <svg className="portal-search-bar__icon-search" aria-hidden="true">
            <use href="/dist/assets/icons/sprite.svg#search"></use>
          </svg>
        </Button>
      </div>
    </div>
  );
}
